import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import {
    ACCOUNT_ITEM_RADIUS,
    ACCOUNT_ITEM_TEXT_COLOR,
    ACCOUNT_MENU_METRICS,
    accountTypography,
    startsAccountSessionGroup,
} from '%evp_theme/brandbook/account/accountMenu';
import MaterialSymbol from '../MaterialSymbol';
import AccountRoleStrip from './AccountRoleStrip';
import buildAccountActions, { accountActionLinkProps } from './buildAccountActions';
import { accountShape, headerTranslationsShape } from './propTypes';
import { destinationIconColor } from './accountDestinationTreatments';
import FOCUS_RING_SX from '../focusRingSx';
import { HOVERED_ROW_SX, SUBTLE_BORDER_COLOR } from '../highContrastSx';

const TRIGGER_ID = 'redesign-header-account-trigger';

const {
    radius: MENU_RADIUS,
    triggerIconSize: TRIGGER_ICON_SIZE,
    triggerChevronSize: TRIGGER_CHEVRON_SIZE,
    itemIconSize: ITEM_ICON_SIZE,
} = ACCOUNT_MENU_METRICS;

function buildChevronSx(isOpen) {
    return {
        transition: 'transform 0.15s',
        transform: isOpen ? 'rotate(180deg)' : 'none',
    };
}

function buildTriggerSx(isOpen) {
    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        pl: 0.5,
        pr: 0.25,
        py: 0.5,
        borderRadius: 999,
        border: 'none',
        background: 'none',
        fontFamily: 'inherit',
        cursor: 'pointer',
        flexShrink: 0,
        color: isOpen ? 'primary.main' : 'grey.500',
        bgcolor: isOpen ? 'grey.50' : 'transparent',
        transition: 'color 0.15s, background-color 0.15s',
        '&:hover': { color: 'primary.main', ...HOVERED_ROW_SX },
        '&:focus-visible': { ...FOCUS_RING_SX, color: 'primary.main', bgcolor: 'grey.50' },
    };
}

const MENU_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'right' };
const MENU_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'right' };

const MENU_GAP_BELOW_TRIGGER = '8px';

const MENU_PAPER_PROPS = {
    sx: {
        borderRadius: `${MENU_RADIUS}px`,
        border: '1px solid',
        borderColor: SUBTLE_BORDER_COLOR,
        boxShadow: ACCOUNT_MENU_METRICS.shadow,
        mt: MENU_GAP_BELOW_TRIGGER,
        minWidth: ACCOUNT_MENU_METRICS.minWidth,
        maxWidth: ACCOUNT_MENU_METRICS.maxWidth,
        overflow: 'visible',
    },
};

const MENU_LIST_SX = {
    py: 1,
};

const ITEM_SX = theme => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    mx: 1,
    px: 1.25,
    py: 1.125,
    borderRadius: `${ACCOUNT_ITEM_RADIUS}px`,
    color: ACCOUNT_ITEM_TEXT_COLOR,
    ...accountTypography(theme, 'item'),
    transition: 'background-color 0.15s',
    '&:hover': HOVERED_ROW_SX,
});

function buildItemIconSx(iconColor) {
    return {
        display: 'flex',
        flexShrink: 0,
        color: destinationIconColor(iconColor),
    };
}

const DIVIDER_SX = { my: 1, mx: 2, borderColor: SUBTLE_BORDER_COLOR };

function AccountMenu({
    account,
    myTicketsUrl = '',
    manageEventsUrl = '',
    translations,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const isOpen = Boolean(anchorEl);
    const actions = buildAccountActions({
        myTicketsUrl,
        manageEventsUrl,
        userSettingsUrl: account.settingsUrl,
        logoutUrl: account.logoutUrl,
        stopImpersonatingUrl: account.stopImpersonatingUrl,
        translations,
    });

    const close = () => setAnchorEl(null);

    return (
        <>
            <Box
                component="button"
                type="button"
                id={TRIGGER_ID}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-label={translations.profileLabel}
                onClick={event => setAnchorEl(event.currentTarget)}
                sx={buildTriggerSx(isOpen)}
                data-testid="redesign-header-account-trigger"
                data-role={account.role}
            >
                <MaterialSymbol name="account_circle" size={TRIGGER_ICON_SIZE} />
                <MaterialSymbol name="expand_more" size={TRIGGER_CHEVRON_SIZE} sx={buildChevronSx(isOpen)} />
            </Box>
            <Popover
                anchorEl={anchorEl}
                open={isOpen}
                onClose={close}
                disableScrollLock
                anchorOrigin={MENU_ANCHOR_ORIGIN}
                transformOrigin={MENU_TRANSFORM_ORIGIN}
                PaperProps={MENU_PAPER_PROPS}
            >
                <AccountRoleStrip
                    role={account.role}
                    fullName={account.fullName}
                    email={account.email}
                    translations={translations}
                    floating
                />
                <MenuList
                    autoFocusItem={isOpen}
                    aria-labelledby={TRIGGER_ID}
                    sx={MENU_LIST_SX}
                    data-testid="redesign-header-account-menu"
                >
                    {actions.flatMap((action, index) => [
                        startsAccountSessionGroup(actions, index)
                            ? <Divider key={`${action.key}-divider`} sx={DIVIDER_SX} />
                            : null,
                        (
                            <MenuItem
                                key={action.key}
                                {...accountActionLinkProps(action)}
                                onClick={close}
                                sx={ITEM_SX}
                                data-testid={`redesign-header-account-${action.key}`}
                            >
                                <Box sx={buildItemIconSx(action.iconColor)}>
                                    <MaterialSymbol name={action.icon} size={ITEM_ICON_SIZE} />
                                </Box>
                                {action.label}
                            </MenuItem>
                        ),
                    ]).filter(Boolean)}
                </MenuList>
            </Popover>
        </>
    );
}

AccountMenu.propTypes = {
    account: accountShape.isRequired,
    myTicketsUrl: PropTypes.string,
    manageEventsUrl: PropTypes.string,
    translations: headerTranslationsShape.isRequired,
};

export default AccountMenu;
